import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'

interface Dia {
  seq: number
  data: string
  tipo: number | null
  jornada: number | null
  en1: number | null; sa1: number | null
  en2: number | null; sa2: number | null
  en3: number | null; sa3: number | null
  en4: number | null; sa4: number | null
  en5: number | null; sa5: number | null
  en6: number | null; sa6: number | null
  en7: number | null; sa7: number | null
  en8: number | null; sa8: number | null
  quebra_jornada: number | null
  tempo_total_intervalo: number | null
  hora_trabalhada: number | null
  hora_compensar: number | null
  he50: number | null
  he100: number | null
  adicional_noturno: number | null
  he_noturna: number | null
}

interface PeriodoBanco {
  dias: number
  saldoMinutos: number
}

interface Ficha {
  ano: number
  mes: number
  colaborador: string
  nome: string
  situacao: number | null
  jornada_padrao: number | null
  dias: Dia[]
  banco_horas: PeriodoBanco[]
  saldo_mes_minutos: number | null
  banco_horas_antes_depois: { antes: PeriodoBanco[]; depois: PeriodoBanco[] } | null
}

interface TipoPonto {
  codigo: number
  descricao: string
  forma_jornada: number
  jornada_especifica: number | null
}

const PARES_PRINCIPAIS = [1, 2, 3, 4] as const
const PARES_EXTRAS = [5, 6, 7, 8] as const

const MESES = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

// linha de título das colunas (Dia/Tipo/Jornada/Atividade.../Q.JOR/TTI...) fixa no
// topo da própria área rolável da tabela — essencial pra não perder a referência de
// qual coluna é qual numa ficha com o mês inteiro de dias
// o fundo azul repete o que "thead tr" já usa no resto do sistema (texto branco por
// cima) — sem isso, o branco do texto padrão fica invisível contra um fundo claro
const ESTILO_COLUNA_FIXA: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 15, background: '#1a6ea8' }

// tonalidade de fundo por dupla entrada/saída, pra facilitar a leitura visual dos pares
const TOM_DUPLA: Record<number, string> = {
  1: '#e8f8ee', 2: '#e9f1fb', 3: '#fdeef6', 4: '#fef8e3',
  5: '#f1ecfd', 6: '#fdece2', 7: '#e3f8f6', 8: '#f2f1e6',
}

// colunas calculadas (o cálculo em si vem numa etapa futura, por ora só exibem o valor gravado)
const CAMPOS_CALCULADOS = [
  { campo: 'quebra_jornada', sigla: 'Q.JOR', descricao: 'Quebra de Jornada' },
  { campo: 'tempo_total_intervalo', sigla: 'TTI', descricao: 'Tempo Total de Intervalo' },
  { campo: 'hora_trabalhada', sigla: 'HRTB', descricao: 'Hora Trabalhada' },
  { campo: 'hora_compensar', sigla: 'HC', descricao: 'Hora a Compensar' },
  { campo: 'he50', sigla: 'HE50', descricao: 'Hora Extra 50%' },
  { campo: 'he100', sigla: 'HE100', descricao: 'Hora Extra 100%' },
  { campo: 'adicional_noturno', sigla: 'ADN', descricao: 'Adicional Noturno' },
  { campo: 'he_noturna', sigla: 'HEN', descricao: 'Hora Extra Noturna' },
] as const

// campos que entram na linha de totais do cabeçalho (Q.JOR e TTI ficam de fora,
// pois são referentes à quebra entre dias/intervalos, não somas do período)
const CAMPOS_TOTAL = CAMPOS_CALCULADOS.filter(c => c.campo !== 'quebra_jornada' && c.campo !== 'tempo_total_intervalo')

function SituacaoBadge({ situacao }: { situacao: number | null }) {
  if (situacao === 0) return <span className="badge badge-success">Aberto</span>
  if (situacao === 1) return <span className="badge badge-default">Fechado</span>
  return <span className="badge badge-default">—</span>
}

function numeroParaHM(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  const horas = Math.floor(v)
  const minutos = Math.round((v - horas) * 100)
  return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`
}

// nas colunas calculadas (só leitura), 0:00 é tratado como "nada a mostrar" — mesmo
// traço já usado para os valores nulos, pra não poluir a tela de números zerados
function formatarColunaCalculada(v: number | null | undefined): string {
  if (v === null || v === undefined || Number(v) === 0) return '—'
  return numeroParaHM(v)
}

function hmParaNumero(hm: string): number | null {
  if (!hm.trim()) return null
  const [h, m] = hm.split(':')
  return Number(h || 0) + Number(m || 0) / 100
}

// mesma lógica de conversão do backend (H.MM não é fração decimal, os 2 últimos
// dígitos são minutos de verdade), usada aqui só pra somar os totais corretamente
function horaNumericaParaMinutos(v: number): number {
  const h = Math.floor(v)
  const m = Math.round((v - h) * 100)
  return h * 60 + m
}

function minutosParaHM(totalMin: number): string {
  const sinal = totalMin < 0 ? '-' : ''
  const abs = Math.abs(totalMin)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${sinal}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Interpreta o que a pessoa digitou num campo de horário (730, 0730, 07,30, 07:30, 07.30 ...)
// e devolve o horário no padrão HH:MM. Os últimos dois dígitos digitados são sempre os minutos;
// os dígitos anteriores são as horas (o zero na frente é opcional). 24:00 é aceito como 00:00.
// Retorna null quando o horário é inválido (ex: 50:54).
function interpretarHora(digitado: string): string | null {
  const digitos = digitado.replace(/\D/g, '')
  if (!digitos) return ''
  const minutos = Number(digitos.slice(-2))
  let horas = Number(digitos.slice(0, -2) || '0')
  if (horas === 24 && minutos === 0) horas = 0
  if (horas > 23 || minutos > 59) return null
  return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`
}

function formatarData(iso: string) {
  const [ano, mes, dia] = iso.slice(0, 10).split('-')
  return `${dia}/${mes}/${ano}`
}

function diaSemana(iso: string) {
  const dias = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB']
  return dias[new Date(iso.slice(0, 10) + 'T00:00:00').getDay()]
}

function CampoHora({ valorInicial, tom, interpretar, disabled, seq, campo, onSalvar }: {
  valorInicial: string
  tom?: string
  interpretar?: boolean
  disabled?: boolean
  seq?: number
  campo?: string
  onSalvar: (valor: string) => void
}) {
  const [valor, setValor] = useState(valorInicial)
  useEffect(() => { setValor(valorInicial) }, [valorInicial])

  function tratarBlur() {
    if (valor === valorInicial) return
    if (!interpretar) {
      onSalvar(valor)
      return
    }
    const interpretado = interpretarHora(valor)
    if (interpretado === null) {
      alert(`Horário inválido: "${valor}". Digite um horário entre 00:00 e 23:59.`)
      setValor(valorInicial)
      return
    }
    setValor(interpretado)
    onSalvar(interpretado)
  }

  return (
    <input
      type="text"
      value={valor}
      placeholder="--:--"
      disabled={disabled}
      data-seq={seq}
      data-campo={campo}
      style={{ width: 56, textAlign: 'center', background: tom }}
      onChange={e => setValor(e.target.value)}
      onBlur={tratarBlur}
    />
  )
}

// Par de entrada/saída de uma atividade. Só valida e salva quando o foco sai do par
// inteiro (não a cada campo), pra dar tempo da pessoa preencher entrada e saída antes
// de conferir as regras: par tem que estar completo (os dois ou nenhum), e as atividades
// anteriores precisam já estar preenchidas (não pode deixar uma no meio em branco).
function ParAtividade({ dia, p, tom, bloqueado, parClipboard, pendente, onSalvarPar, onCopiarPar, onTogglePendentePar, onLimparPar }: {
  dia: Dia
  p: number
  tom: string
  bloqueado?: boolean
  parClipboard: { seq: number; p: number } | null
  pendente: boolean
  onSalvarPar: (seq: number, valores: Record<string, number | null>) => void
  onCopiarPar: (dia: Dia, p: number) => void
  onTogglePendentePar: (dia: Dia, p: number) => void
  onLimparPar: (dia: Dia, p: number) => void
}) {
  const valorEnInicial = numeroParaHM((dia as any)[`en${p}`])
  const valorSaInicial = numeroParaHM((dia as any)[`sa${p}`])
  const [entrada, setEntrada] = useState(valorEnInicial)
  const [saida, setSaida] = useState(valorSaInicial)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setEntrada(valorEnInicial) }, [valorEnInicial])
  useEffect(() => { setSaida(valorSaInicial) }, [valorSaInicial])

  function normalizar(valor: string, valorAnterior: string, setter: (v: string) => void) {
    if (valor === valorAnterior) return valor
    const interpretado = interpretarHora(valor)
    if (interpretado === null) {
      alert(`Horário inválido: "${valor}". Digite um horário entre 00:00 e 23:59.`)
      setter(valorAnterior)
      return valorAnterior
    }
    setter(interpretado)
    return interpretado
  }

  function aoSairDoPar(e: React.FocusEvent<HTMLDivElement>) {
    if (e.relatedTarget && containerRef.current?.contains(e.relatedTarget as Node)) return

    let novaEntrada = normalizar(entrada, valorEnInicial, setEntrada)
    let novaSaida = normalizar(saida, valorSaInicial, setSaida)
    if (novaEntrada === valorEnInicial && novaSaida === valorSaInicial) return

    if (Boolean(novaEntrada) !== Boolean(novaSaida)) {
      alert(`Atividade ${p}: informe a entrada e a saída juntas. Como só um dos horários foi preenchido, o lançamento foi descartado.`)
      novaEntrada = ''
      novaSaida = ''
      setEntrada('')
      setSaida('')
    } else if (novaEntrada && novaSaida) {
      for (let i = 1; i < p; i++) {
        if ((dia as any)[`en${i}`] === null || (dia as any)[`sa${i}`] === null) {
          alert(`Preencha a Atividade ${i} antes de lançar a Atividade ${p}.`)
          novaEntrada = ''
          novaSaida = ''
          setEntrada('')
          setSaida('')
          break
        }
      }
    }

    onSalvarPar(dia.seq, {
      [`en${p}`]: hmParaNumero(novaEntrada),
      [`sa${p}`]: hmParaNumero(novaSaida),
    })
  }

  const ehOrigemClipboard = parClipboard?.seq === dia.seq && parClipboard?.p === p
  // 23:59 costuma vir do BUSREP como marcador de "ainda em aberto" (turno sem
  // fechamento real), não um horário de saída de verdade — destaca em vermelho
  // pra chamar atenção do digitador
  const entradaSuspeita = entrada === '23:59'
  const saidaSuspeita = saida === '23:59'

  return (
    <div ref={containerRef} onBlur={aoSairDoPar} style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
      <input
        type="text"
        value={entrada}
        placeholder="--:--"
        disabled={bloqueado}
        data-seq={dia.seq}
        data-campo={`en${p}`}
        title={entradaSuspeita ? '23:59 normalmente indica um turno sem horário de saída real — confira' : undefined}
        style={{
          width: 56, textAlign: 'center',
          background: entradaSuspeita ? '#fee2e2' : tom,
          border: entradaSuspeita ? '1px solid #ef4444' : undefined,
          color: entradaSuspeita ? '#b91c1c' : undefined,
        }}
        onChange={e => setEntrada(e.target.value)}
      />
      <input
        type="text"
        value={saida}
        placeholder="--:--"
        disabled={bloqueado}
        data-seq={dia.seq}
        data-campo={`sa${p}`}
        title={saidaSuspeita ? '23:59 normalmente indica um turno sem horário de saída real — confira' : undefined}
        style={{
          width: 56, textAlign: 'center',
          background: saidaSuspeita ? '#fee2e2' : tom,
          border: saidaSuspeita ? '1px solid #ef4444' : undefined,
          color: saidaSuspeita ? '#b91c1c' : undefined,
        }}
        onChange={e => setSaida(e.target.value)}
      />
      {!bloqueado && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <button
            type="button"
            tabIndex={-1}
            title={
              ehOrigemClipboard ? 'Copiado — marque outras atividades como destino'
              : parClipboard ? (pendente ? 'Marcado como destino — clique pra desmarcar' : 'Marcar esta atividade como destino da cópia')
              : 'Copiar este horário'
            }
            disabled={ehOrigemClipboard}
            onClick={() => parClipboard ? onTogglePendentePar(dia, p) : onCopiarPar(dia, p)}
            style={{
              fontSize: 11, lineHeight: 1, padding: '1px 3px', border: 'none', borderRadius: 4,
              background: pendente ? '#dbeafe' : 'transparent', cursor: ehOrigemClipboard ? 'default' : 'pointer',
              color: ehOrigemClipboard ? '#2563eb' : pendente ? '#1d4ed8' : '#9ca3af',
            }}
          >
            {ehOrigemClipboard ? '✓' : parClipboard ? (pendente ? '☑️' : '📥') : '📋'}
          </button>
          <button
            type="button"
            tabIndex={-1}
            title="Excluir esta atividade"
            onClick={() => onLimparPar(dia, p)}
            style={{
              fontSize: 11, lineHeight: 1, padding: '1px 3px', border: 'none', borderRadius: 4,
              background: 'transparent', cursor: 'pointer', color: '#9ca3af',
            }}
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  )
}

function LinhaDia({
  dia, linhaPar, tipos, mostrarExtras, bloqueado, parClipboard, parPendentes, diaClipboard, diaSelecionado,
  onSalvarCampo, onSalvarPar, onTrocarTipo, onCopiarPar, onTogglePendentePar, onLimparPar, onCopiarDia, onToggleSelecionado, onExcluir,
}: {
  dia: Dia
  linhaPar: boolean
  tipos: TipoPonto[]
  mostrarExtras: boolean
  bloqueado: boolean
  parClipboard: { seq: number; p: number } | null
  parPendentes: Set<string>
  diaClipboard: { origemSeq: number } | null
  diaSelecionado: boolean
  onSalvarCampo: (seq: number, campo: string, valorHM: string) => void
  onSalvarPar: (seq: number, valores: Record<string, number | null>) => void
  onTrocarTipo: (dia: Dia, novoTipo: number) => void
  onCopiarPar: (dia: Dia, p: number) => void
  onTogglePendentePar: (dia: Dia, p: number) => void
  onLimparPar: (dia: Dia, p: number) => void
  onCopiarDia: (dia: Dia) => void
  onToggleSelecionado: (seq: number) => void
  onExcluir: (dia: Dia) => void
}) {
  const pares = mostrarExtras ? [...PARES_PRINCIPAIS, ...PARES_EXTRAS] : PARES_PRINCIPAIS
  const ehOrigemDia = diaClipboard?.origemSeq === dia.seq

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9', background: diaSelecionado ? '#eff6ff' : (linhaPar ? '#ffffff' : '#eef2f7') }}>
      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
        {diaClipboard && !ehOrigemDia && (
          <input
            type="checkbox"
            checked={diaSelecionado}
            onChange={() => onToggleSelecionado(dia.seq)}
            style={{ width: 16, height: 16, minWidth: 16, marginRight: 6, verticalAlign: 'middle' }}
            title="Marcar como destino da cópia"
          />
        )}
        {formatarData(dia.data)} <span style={{ color: '#9ca3af' }}>{diaSemana(dia.data)}</span>
      </td>
      <td style={{ padding: '4px 8px' }}>
        {/* tipo null (dia de fichas antigas/importadas, de antes desse campo sempre vir
            preenchido) é "Normal" igual a tipo 0 em todo o resto do sistema (cálculo,
            impressão) — aqui tem que valer o mesmo, senão a caixinha mostra uma opção
            vazia sem rótulo em vez de "Normal" selecionado. */}
        <select
          value={dia.tipo ?? 0}
          disabled={bloqueado}
          style={{ fontSize: '0.8rem' }}
          onChange={e => onTrocarTipo(dia, Number(e.target.value))}
        >
          {tipos.map(t => <option key={t.codigo} value={t.codigo}>{t.descricao}</option>)}
        </select>
      </td>
      <td style={{ padding: '4px 8px' }}>
        <CampoHora
          valorInicial={numeroParaHM(dia.jornada)}
          disabled={bloqueado}
          seq={dia.seq}
          campo="jornada"
          onSalvar={v => onSalvarCampo(dia.seq, 'jornada', v)}
        />
      </td>
      {pares.map(p => (
        <td key={p} style={{ padding: '4px 4px', textAlign: 'center' }}>
          <ParAtividade
            dia={dia} p={p} tom={TOM_DUPLA[p]}
            bloqueado={bloqueado}
            parClipboard={parClipboard}
            pendente={parPendentes.has(`${dia.seq}:${p}`)}
            onSalvarPar={onSalvarPar}
            onCopiarPar={onCopiarPar}
            onTogglePendentePar={onTogglePendentePar}
            onLimparPar={onLimparPar}
          />
        </td>
      ))}
      {CAMPOS_CALCULADOS.map(({ campo }) => (
        <td key={campo} style={{ padding: '4px 8px', textAlign: 'center', color: '#6b7280' }}>
          {formatarColunaCalculada((dia as any)[campo])}
        </td>
      ))}
      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
        {!bloqueado && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn btn-outline btn-sm"
              style={{ whiteSpace: 'nowrap' }}
              title="Copiar as atividades deste dia para outros dias"
              onClick={() => onCopiarDia(dia)}
            >
              📋 {ehOrigemDia ? 'Copiado' : 'Copiar dia'}
            </button>
            <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c', whiteSpace: 'nowrap' }} onClick={() => onExcluir(dia)}>Excluir</button>
          </div>
        )}
      </td>
    </tr>
  )
}

export default function CartaoPontoDias() {
  const { ano, mes, colaborador } = useParams()
  const navigate = useNavigate()
  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [tipos, setTipos] = useState<TipoPonto[]>([])
  const [novaData, setNovaData] = useState('')
  const [mostrarExtras, setMostrarExtras] = useState(false)
  const [parClipboard, setParClipboard] = useState<{ seq: number; p: number; en: number | null; sa: number | null } | null>(null)
  const [parPendentes, setParPendentes] = useState<Set<string>>(new Set())
  const [diaClipboard, setDiaClipboard] = useState<{ origemSeq: number; valores: Record<string, number | null> } | null>(null)
  const [diasSelecionados, setDiasSelecionados] = useState<Set<number>>(new Set())
  const [eventoAtivo, setEventoAtivo] = useState(false)
  const [eventoTipo, setEventoTipo] = useState(0)
  const [eventoDataInicio, setEventoDataInicio] = useState('')
  const [eventoDataFim, setEventoDataFim] = useState('')
  const [importAtivo, setImportAtivo] = useState(false)
  const [importDataInicio, setImportDataInicio] = useState('')
  const [importDataFim, setImportDataFim] = useState('')
  const [importSobrescrever, setImportSobrescrever] = useState(false)
  const [importando, setImportando] = useState(false)
  const [imprimindo, setImprimindo] = useState(false)
  const [ampliado, setAmpliado] = useState(false)

  const totais = useMemo(() => {
    const somaMin: Record<string, number> = { jornada: 0 }
    for (const { campo } of CAMPOS_TOTAL) somaMin[campo] = 0
    for (const d of ficha?.dias ?? []) {
      if (d.jornada !== null && d.jornada !== undefined) somaMin.jornada += horaNumericaParaMinutos(Number(d.jornada))
      for (const { campo } of CAMPOS_TOTAL) {
        const v = (d as any)[campo]
        if (v !== null && v !== undefined) somaMin[campo] += horaNumericaParaMinutos(Number(v))
      }
    }
    return somaMin
  }, [ficha?.dias])

  // detecta turnos 5-8 preenchidos só na primeira carga da ficha, pra não sobrescrever
  // a escolha do usuário de ocultar os turnos extras a cada recarregamento (ex: após
  // salvar um campo)
  const detectouExtrasRef = useRef(false)

  // A rolagem da tabela precisa acontecer inteiramente DENTRO do container da tabela,
  // nunca na página — se a página também tiver o que rolar (mesmo que só uns pixels a
  // mais que a altura da tela), o scroll do mouse esgota o scroll interno e passa a
  // rolar a página, arrastando o container (e o thead fixo nele) pra fora da tela junto.
  // Por isso a raiz do componente ganha altura FIXA (tela inteira menos o que já está
  // acima dela), em vez de um "maxHeight" aproximado — assim a página nunca rola, e é
  // garantido que só o container da tabela (flex:1 + overflow:auto lá embaixo) rola.
  const raizRef = useRef<HTMLDivElement>(null)
  const [alturaDisponivel, setAlturaDisponivel] = useState<number | null>(null)

  useLayoutEffect(() => {
    function medir() {
      const elemento = raizRef.current
      if (!elemento) return
      setAlturaDisponivel(window.innerHeight - elemento.getBoundingClientRect().top)
    }
    medir()
    window.addEventListener('resize', medir)
    return () => window.removeEventListener('resize', medir)
    // enquanto a ficha ainda não carregou, o componente retorna null (raizRef nunca chega
    // a existir); precisa medir de novo assim que ela aparecer pela 1ª vez — senão a
    // medição roda uma única vez contra um ref vazio e a altura fica sempre indefinida.
    // Também remede ao ligar/desligar o modo ampliado: a raiz passa a ocupar a tela
    // inteira (top:0) em vez do espaço abaixo do topbar, então a altura disponível muda.
  }, [Boolean(ficha), ampliado])

  async function carregar() {
    const r = await api.get(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}`)
    setFicha(r.data)
    if (!detectouExtrasRef.current) {
      detectouExtrasRef.current = true
      if (r.data.dias.some((d: Dia) => PARES_EXTRAS.some(p => (d as any)[`en${p}`] !== null || (d as any)[`sa${p}`] !== null))) {
        setMostrarExtras(true)
      }
    }
  }

  useEffect(() => {
    detectouExtrasRef.current = false
    carregar()
    api.get('/cartao-ponto/tipos-ponto').then(r => setTipos(r.data))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano, mes, colaborador])

  async function salvarCampo(seq: number, campo: string, valorHM: string) {
    if (campo !== 'tipo' && Math.abs(hmParaNumero(valorHM) ?? 0) >= 100) {
      alert('Horário inválido: use um valor entre -99:59 e 99:59.')
      await carregar()
      return
    }
    const body: Record<string, any> = campo === 'tipo'
      ? { tipo: Number(valorHM) }
      : { [campo]: hmParaNumero(valorHM) }
    setFicha(f => f && {
      ...f,
      dias: f.dias.map(d => d.seq === seq ? { ...d, [campo]: campo === 'tipo' ? Number(valorHM) : hmParaNumero(valorHM) } : d),
    })
    try {
      await api.put(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/dias/${seq}`, body)
      await carregar()
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao salvar o campo.')
      await carregar()
    }
  }

  async function salvarPar(seq: number, valores: Record<string, number | null>) {
    setFicha(f => f && {
      ...f,
      dias: f.dias.map(d => d.seq === seq ? { ...d, ...valores } : d),
    })
    try {
      await api.put(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/dias/${seq}`, valores)
      await carregar()
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao salvar o horário.')
      await carregar()
    }
  }

  async function trocarTipo(dia: Dia, novoTipo: number) {
    const config = tipos.find(t => t.codigo === novoTipo)
    const novaJornada = config?.forma_jornada === 1 ? config.jornada_especifica : (ficha?.jornada_padrao ?? null)
    await salvarPar(dia.seq, { tipo: novoTipo, jornada: novaJornada })
  }

  function copiarPar(dia: Dia, p: number) {
    setDiaClipboard(null)
    setDiasSelecionados(new Set())
    setParPendentes(new Set())
    setParClipboard({ seq: dia.seq, p, en: (dia as any)[`en${p}`], sa: (dia as any)[`sa${p}`] })
  }

  function togglePendentePar(dia: Dia, p: number) {
    const key = `${dia.seq}:${p}`
    setParPendentes(s => {
      const novo = new Set(s)
      if (novo.has(key)) novo.delete(key); else novo.add(key)
      return novo
    })
  }

  async function confirmarColagemPar() {
    if (!ficha || !parClipboard) return
    for (const key of parPendentes) {
      const [seqStr, pStr] = key.split(':')
      const seq = Number(seqStr)
      const p = Number(pStr)
      const dia = ficha.dias.find(d => d.seq === seq)
      if (!dia) continue
      let bloqueado = false
      for (let i = 1; i < p; i++) {
        if ((dia as any)[`en${i}`] === null || (dia as any)[`sa${i}`] === null) {
          alert(`Preencha a Atividade ${i} antes de colar na Atividade ${p} (dia ${formatarData(dia.data)}).`)
          bloqueado = true
          break
        }
      }
      if (bloqueado) continue
      await salvarPar(seq, { [`en${p}`]: parClipboard.en, [`sa${p}`]: parClipboard.sa })
    }
    setParClipboard(null)
    setParPendentes(new Set())
  }

  async function limparPar(dia: Dia, p: number) {
    await salvarPar(dia.seq, { [`en${p}`]: null, [`sa${p}`]: null })
  }

  function copiarDia(dia: Dia) {
    setParClipboard(null)
    const valores: Record<string, number | null> = {}
    for (const p of [...PARES_PRINCIPAIS, ...PARES_EXTRAS]) {
      valores[`en${p}`] = (dia as any)[`en${p}`]
      valores[`sa${p}`] = (dia as any)[`sa${p}`]
    }
    setDiaClipboard({ origemSeq: dia.seq, valores })
    setDiasSelecionados(new Set())
  }

  function toggleSelecionado(seq: number) {
    setDiasSelecionados(s => {
      const novo = new Set(s)
      if (novo.has(seq)) novo.delete(seq); else novo.add(seq)
      return novo
    })
  }

  async function colarNosDiasSelecionados() {
    if (!diaClipboard || diasSelecionados.size === 0) return
    for (const seq of diasSelecionados) {
      await salvarPar(seq, diaClipboard.valores)
    }
    setDiaClipboard(null)
    setDiasSelecionados(new Set())
  }

  function cancelarCopia() {
    setParClipboard(null)
    setParPendentes(new Set())
    setDiaClipboard(null)
    setDiasSelecionados(new Set())
  }

  async function excluirDia(d: Dia) {
    if (!window.confirm(`Excluir o dia ${formatarData(d.data)}?`)) return
    await api.delete(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/dias/${d.seq}`)
    await carregar()
  }

  async function adicionarDia() {
    if (!novaData) return
    await api.post(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/dias`, { data: novaData })
    setNovaData('')
    await carregar()
  }

  async function aplicarEvento() {
    if (!eventoDataInicio || !eventoDataFim) return
    if (new Date(eventoDataFim) < new Date(eventoDataInicio)) {
      alert('A data final não pode ser anterior à data inicial.')
      return
    }
    try {
      await api.put(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/evento`, {
        tipo: eventoTipo, dataInicio: eventoDataInicio, dataFim: eventoDataFim,
      })
      setEventoAtivo(false)
      setEventoDataInicio('')
      setEventoDataFim('')
      await carregar()
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao lançar o evento.')
    }
  }

  function abrirImportacao() {
    if (ficha && ficha.dias.length > 0) {
      const datas = ficha.dias.map(d => d.data.slice(0, 10)).sort()
      setImportDataInicio(datas[0])
      setImportDataFim(datas[datas.length - 1])
    }
    setImportAtivo(true)
  }

  async function importarBusrep() {
    if (!importDataInicio || !importDataFim) return
    if (new Date(importDataFim) < new Date(importDataInicio)) {
      alert('A data final não pode ser anterior à data inicial.')
      return
    }
    setImportando(true)
    try {
      const r = await api.post(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/importar-busrep`, {
        dataInicio: importDataInicio, dataFim: importDataFim, sobrescrever: importSobrescrever,
      })
      const { registrosEncontrados, camposAtualizados, camposIgnorados, diasNaoEncontrados } = r.data
      let msg = `Registros encontrados no BUSREP: ${registrosEncontrados}\nCampos atualizados: ${camposAtualizados}\nCampos ignorados (já preenchidos): ${camposIgnorados}`
      if (diasNaoEncontrados?.length > 0) msg += `\nDias fora da ficha (ignorados): ${diasNaoEncontrados.join(', ')}`
      alert(msg)
      setImportAtivo(false)
      await carregar()
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao importar do BUSREP.')
    } finally {
      setImportando(false)
    }
  }

  async function fecharFicha() {
    if (!ficha) return
    const saldoMes = totais.he50 + totais.he100 - totais.hora_compensar
    const resumo = `Jornada total: ${minutosParaHM(totais.jornada)}\nHRTB total: ${minutosParaHM(totais.hora_trabalhada)}\n`
      + `Saldo do mês (HE50+HE100−HC): ${minutosParaHM(saldoMes)}`
    if (!window.confirm(`Fechar a ficha de ${mes}/${ano}?\n\n${resumo}\n\nApós fechar, os lançamentos ficam bloqueados e o banco de horas do colaborador é atualizado.`)) return
    try {
      await api.post(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/fechar`)
      await carregar()
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao fechar a ficha.')
    }
  }

  async function reabrirFicha() {
    if (!window.confirm('Reabrir esta ficha? Isso desfaz as alterações no banco de horas geradas por este fechamento.')) return
    try {
      await api.post(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/reabrir`)
      await carregar()
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao reabrir a ficha.')
    }
  }

  async function ressincronizarJornada() {
    if (!window.confirm('Ressincronizar a jornada dos dias com a configuração atual de cada tipo de jornada?\n\nIsso regrava a jornada dos dias cujo tipo foi editado depois do lançamento, e recalcula HC/HRTB/HE desses dias.')) return
    try {
      const r = await api.post(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/ressincronizar-jornada`)
      await carregar()
      alert(r.data.diasAtualizados > 0 ? `${r.data.diasAtualizados} dia(s) atualizado(s).` : 'Nenhum dia estava desatualizado.')
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Erro ao ressincronizar a jornada.')
    }
  }

  async function imprimirFicha() {
    setImprimindo(true)
    try {
      const r = await api.get(`/cartao-ponto/fichas/${ano}/${mes}/${colaborador}/pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (e: any) {
      alert('Erro ao gerar o extrato de horas.')
    } finally {
      setImprimindo(false)
    }
  }

  // Tab já percorre os campos de horário lado a lado dentro da linha (ordem natural do
  // DOM, pulando os botões via tabIndex=-1). Enter desce pro dia seguinte, sempre voltando
  // pra entrada da Atividade 1 — não pro mesmo campo em que estava — porque o fluxo de
  // digitação é sequencial (dia inteiro, atividade por atividade), não coluna por coluna.
  function aoTeclarNaGrade(e: React.KeyboardEvent<HTMLTableElement>) {
    if (e.key !== 'Enter' || !ficha) return
    const alvo = e.target as HTMLElement
    const seqAtual = alvo.dataset.seq
    if (!seqAtual || !alvo.dataset.campo) return
    e.preventDefault()
    const indice = ficha.dias.findIndex(d => d.seq === Number(seqAtual))
    if (indice === -1 || indice === ficha.dias.length - 1) return
    const proximoSeq = ficha.dias[indice + 1].seq
    const proximo = document.querySelector<HTMLInputElement>(`input[data-seq="${proximoSeq}"][data-campo="en1"]`)
    proximo?.focus()
  }

  if (!ficha) return null

  const pares = mostrarExtras ? [...PARES_PRINCIPAIS, ...PARES_EXTRAS] : PARES_PRINCIPAIS
  const bloqueado = ficha.situacao === 1

  return (
    <div
      ref={raizRef}
      style={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        height: alturaDisponivel !== null ? `${alturaDisponivel}px` : undefined,
        // Modo ampliado: um overlay cobrindo a janela inteira, por cima do menu lateral e
        // do topbar (que continuam lá, só ficam visualmente encobertos) — bem mais simples
        // que erguer esse estado até o Layout pra esconder os dois de verdade.
        ...(ampliado ? {
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgb(var(--surface-muted))', padding: '1.5rem',
        } : {}),
      }}
    >
      {!ampliado && (
      <div className="card" style={{ marginBottom: '1.2rem', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              {ficha.colaborador} - {ficha.nome}
              <SituacaoBadge situacao={ficha.situacao} />
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Período de referência: {mes}/{ano}
              <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', marginLeft: 8 }} title="Tabela frota.carttb">CARTTB</span>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: '0.8rem', color: '#374151', flexWrap: 'wrap' }}>
              <span>
                <abbr title="Jornada" style={{ textDecoration: 'underline dotted', cursor: 'help', color: '#6b7280' }}>Jornada</abbr>
                {' '}<b>{minutosParaHM(totais.jornada)}</b>
              </span>
              {CAMPOS_TOTAL.map(({ campo, sigla, descricao }) => (
                <span key={campo}>
                  <abbr title={descricao} style={{ textDecoration: 'underline dotted', cursor: 'help', color: '#6b7280' }}>{sigla}</abbr>
                  {' '}<b>{minutosParaHM(totais[campo])}</b>
                </span>
              ))}
            </div>
            {ficha.situacao === 1 && ficha.banco_horas_antes_depois ? (
              <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#374151', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#9ca3af' }}>Banco de horas — antes do fechamento:</span>
                  {ficha.banco_horas_antes_depois.antes.length === 0 && <span style={{ color: '#9ca3af' }}>—</span>}
                  {ficha.banco_horas_antes_depois.antes.map(p => (
                    <span key={p.dias}>{p.dias}D <b>{minutosParaHM(p.saldoMinutos)}</b></span>
                  ))}
                </div>
                {ficha.saldo_mes_minutos !== null && (
                  <div>
                    <span style={{ color: '#9ca3af' }}>Saldo {MESES[ficha.mes]}:</span>
                    {' '}<b>{minutosParaHM(ficha.saldo_mes_minutos)}</b>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#9ca3af' }}>Banco de horas — depois do fechamento:</span>
                  {ficha.banco_horas_antes_depois.depois.length === 0 && <span style={{ color: '#9ca3af' }}>—</span>}
                  {ficha.banco_horas_antes_depois.depois.map(p => (
                    <span key={p.dias}>{p.dias}D <b>{minutosParaHM(p.saldoMinutos)}</b></span>
                  ))}
                </div>
              </div>
            ) : ficha.banco_horas.length > 0 && (
              <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: '0.8rem', color: '#374151', flexWrap: 'wrap' }}>
                <span style={{ color: '#9ca3af' }}>Banco de horas:</span>
                {ficha.banco_horas.map(p => (
                  <span key={p.dias}>{p.dias}D <b>{minutosParaHM(p.saldoMinutos)}</b></span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!bloqueado && (
              <>
                <input type="date" value={novaData} onChange={e => setNovaData(e.target.value)} />
                <button className="btn btn-outline" onClick={adicionarDia}>+ Adicionar 1 Dia</button>
                <button className="btn btn-outline" onClick={() => setEventoAtivo(true)}>📅 Lançar Evento</button>
                <button className="btn btn-outline" onClick={abrirImportacao}>📡 Importar BUSREP</button>
                <button className="btn btn-outline" onClick={ressincronizarJornada} title="Regrava a jornada dos dias com a configuração atual de cada tipo de jornada">
                  🔄 Ressincronizar Jornada
                </button>
                <button className="btn btn-outline" onClick={imprimirFicha} disabled={imprimindo}>
                  {imprimindo ? 'Gerando...' : '🖨️ Imprimir'}
                </button>
                <button className="btn btn-primary" onClick={fecharFicha}>Fechar Ficha</button>
              </>
            )}
            {bloqueado && (
              <>
                <button className="btn btn-outline" onClick={imprimirFicha} disabled={imprimindo}>
                  {imprimindo ? 'Gerando...' : '🖨️ Imprimir'}
                </button>
                <button className="btn btn-outline" onClick={reabrirFicha}>Reabrir Ficha</button>
              </>
            )}
            <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto')}>Voltar</button>
          </div>
        </div>
      </div>
      )}

      {eventoAtivo && !ampliado && !bloqueado && (
        <div className="card" style={{ marginBottom: '1.2rem', background: '#fef3e2', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
          <span>📅 Lançar evento no período:</span>
          <select value={eventoTipo} onChange={e => setEventoTipo(Number(e.target.value))} style={{ fontSize: '0.85rem' }}>
            {tipos.map(t => <option key={t.codigo} value={t.codigo}>{t.descricao}</option>)}
          </select>
          <span>de</span>
          <input type="date" value={eventoDataInicio} onChange={e => setEventoDataInicio(e.target.value)} />
          <span>até</span>
          <input type="date" value={eventoDataFim} onChange={e => setEventoDataFim(e.target.value)} />
          <button className="btn btn-primary btn-sm" disabled={!eventoDataInicio || !eventoDataFim} onClick={aplicarEvento}>
            Aplicar
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setEventoAtivo(false)}>Cancelar</button>
        </div>
      )}

      {importAtivo && !ampliado && !bloqueado && (
        <div className="card" style={{ marginBottom: '1.2rem', background: '#eef2ff', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
          <span>📡 Importar do BUSREP — período:</span>
          <input type="date" value={importDataInicio} onChange={e => setImportDataInicio(e.target.value)} />
          <span>até</span>
          <input type="date" value={importDataFim} onChange={e => setImportDataFim(e.target.value)} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={importSobrescrever} onChange={e => setImportSobrescrever(e.target.checked)} />
            Sobrescrever registros já importados
          </label>
          <button className="btn btn-primary btn-sm" disabled={!importDataInicio || !importDataFim || importando} onClick={importarBusrep}>
            {importando ? 'Importando...' : 'Importar'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setImportAtivo(false)}>Cancelar</button>
        </div>
      )}

      {(parClipboard || diaClipboard) && (
        <div className="card" style={{ marginBottom: '1.2rem', background: '#eff6ff', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
          {parClipboard && (
            <>
              <span>📋 Horário da Atividade {parClipboard.p} copiado. Marque com <b>📥</b> as atividades de destino e confirme.</span>
              <button className="btn btn-primary btn-sm" disabled={parPendentes.size === 0} onClick={confirmarColagemPar}>
                Confirmar cópia ({parPendentes.size})
              </button>
            </>
          )}
          {diaClipboard && (
            <>
              <span>📋 Atividades do dia copiadas. Marque os dias de destino e confirme.</span>
              <button className="btn btn-primary btn-sm" disabled={diasSelecionados.size === 0} onClick={colarNosDiasSelecionados}>
                Confirmar cópia ({diasSelecionados.size} dia(s))
              </button>
            </>
          )}
          <button className="btn btn-outline btn-sm" onClick={cancelarCopia}>Cancelar cópia</button>
        </div>
      )}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: '0.6rem', flexShrink: 0 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setMostrarExtras(m => !m)}>
            {mostrarExtras ? '– Ocultar turnos 5–8' : '+ Mostrar turnos 5–8'}
          </button>
          <button
            className="btn btn-outline btn-sm"
            title={ampliado ? 'Reduzir a tela de digitação' : 'Ampliar a tela de digitação (oculta o menu e o cabeçalho da ficha)'}
            onClick={() => setAmpliado(a => !a)}
          >
            {ampliado ? '⛶ Reduzir tela' : '⛶ Ampliar tela'}
          </button>
        </div>
        {/* rolagem própria (não a da página) pra o cabeçalho de colunas conseguir ficar
            fixo de verdade: um container com overflow vira o "chão" do sticky dos filhos.
            A raiz do componente tem altura travada em toda a tela disponível (ver
            alturaDisponivel), então a página em si nunca rola — só este container aqui
            (flex:1 + minHeight:0, o clássico truque pra um filho flex conseguir encolher
            e ganhar scroll em vez de estourar o pai) é que rola de verdade. Sem isso, uma
            rolagem que esgota o scroll interno passa a rolar a página, arrastando o
            container (e o thead fixo nele) pra fora da tela. */}
        <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <table onKeyDown={aoTeclarNaGrade} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...ESTILO_COLUNA_FIXA, padding: '6px 8px' }}>Dia</th>
              <th style={{ ...ESTILO_COLUNA_FIXA, padding: '6px 8px' }}>Tipo</th>
              <th style={{ ...ESTILO_COLUNA_FIXA, padding: '6px 8px' }}>Jornada</th>
              {pares.map(p => (
                <th key={p} style={{ ...ESTILO_COLUNA_FIXA, padding: '6px 8px', textAlign: 'center' }}>
                  Atividade {p}
                </th>
              ))}
              {CAMPOS_CALCULADOS.map(({ campo, sigla, descricao }) => (
                <th key={campo} style={{ ...ESTILO_COLUNA_FIXA, padding: '6px 8px', textAlign: 'center' }}>
                  <abbr title={descricao} style={{ textDecoration: 'underline dotted', cursor: 'help' }}>{sigla}</abbr>
                </th>
              ))}
              <th style={{ ...ESTILO_COLUNA_FIXA, padding: '6px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {ficha.dias.map((d, indice) => (
              <LinhaDia
                key={d.seq}
                dia={d}
                linhaPar={indice % 2 === 0}
                tipos={tipos}
                mostrarExtras={mostrarExtras}
                bloqueado={bloqueado}
                parClipboard={parClipboard}
                parPendentes={parPendentes}
                diaClipboard={diaClipboard}
                diaSelecionado={diasSelecionados.has(d.seq)}
                onSalvarCampo={salvarCampo}
                onTrocarTipo={trocarTipo}
                onSalvarPar={salvarPar}
                onCopiarPar={copiarPar}
                onTogglePendentePar={togglePendentePar}
                onLimparPar={limparPar}
                onCopiarDia={copiarDia}
                onToggleSelecionado={toggleSelecionado}
                onExcluir={excluirDia}
              />
            ))}
          </tbody>
        </table>
        {ficha.dias.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '1rem' }}>Nenhum dia cadastrado.</p>
        )}
        </div>
      </div>
    </div>
  )
}
